<template>
  <div class="booking-confirmation">
    <div class="container-fluid">
      <div class="row no-gutters">
        <div class="col-12 col-lg-4 d-print-none">
          <CiCampgroundInfo
            :campground="campground"
            :confirmation="confirmation"
            :countries="countries"
            :currency="currency"
            :dates="[confirmation.booking_data.date_from, confirmation.booking_data.date_until]"
            :guests="confirmation.booking_data.campsites[0].persons"
            :isCompleted="isCompleted"
            :urls="urls">
            <CiSum
              :campgroundCurrency="campground.currency"
              :confirmation="confirmation"
              :isCompleted="isCompleted"
              :language="language"
              :price="confirmation.pricelist"
              :userCurrency="currency"
              slot="sum">
            </CiSum>
          </CiCampgroundInfo>
        </div>
        <div class="col-12 col-lg-8">
          <div class="booking-confirmation__interaction">
            <CiProgressBar class="hide-on-print" :activeStep="6"></CiProgressBar>

            <div class="booking white-box">
              <div class="row">
                <div class="col-12 col-md-2 d-print-none">
                  <div class="booking__status-icon">
                    <div class="status__circle">
                      <iconCheckmarkWhite class="icon"></iconCheckmarkWhite>
                    </div>
                  </div>
                </div>
                <div class="col-12 col-md-10 print-full">
                  <div class="row">
                    <div class="col-12 col-md-7 print-full">
                      <div class="booking__status-info">
                        <h3 class="booking__headline">
                          {{ isPending
                            ? $t('pages.bookingPartialConfirmation.statusInfo.title')
                            : $t('pages.bookingConfirmation.statusInfo.title')
                          }}
                        </h3>
                        <p>
                          {{ isPending
                            ? $t('pages.bookingPartialConfirmation.statusInfo.content')
                            : $t('pages.bookingConfirmation.statusInfo.content')
                          }}
                        </p>
                      </div>
                    </div>
                    <div class="col-12 col-md-5 print-full">
                      <div class="booking__status-number">
                        <h3 class="booking__headline">
                          {{ $t('pages.bookingConfirmation.statusNumber.title') }}</h3>
                        <span class="booking-number">{{ confirmation.booking_number }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="booking__download d-print-none">
                    <div class="row">
                      <div class="col-12 col-md-7">
                        <div class="booking__status-download-text">
                          <h3 class="booking__headline">
                            {{ $t('pages.bookingConfirmation.download.title') }}</h3>
                        </div>
                      </div>
                      <div class="col-12 col-md-5">
                        <div class="booking__status-download-button">
                          <CiButton class="button--tertiary button--small download-button"
                                    @click="printPage">
                            <span>{{ $t('pages.bookingConfirmation.download.button') }}</span>
                            <iconArrowWhite class="icon icon--animated"></iconArrowWhite>
                          </CiButton>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="booking__download d-print-none">
                    <div class="row">
                      <div class="col-12 col-md-7">
                        <div class="booking__status-download-text">
                          <a
                            :href="icsUrl"
                            download="booking.ics"
                          >
                            <h3 class="booking__headline">
                              {{ $t('pages.bookingConfirmation.cal.title') }}</h3>
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <h2 class="booking-confirmation__subheading">
              {{ $t('pages.bookingConfirmation.detailsHeadline') }}</h2>

            <div class="booking booking--summary white-box">

              <section class="summary__step summary__step--no-border">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t('generic.labels.campground') }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <div class="step__value">
                      <div class="wrapper">
                        <span class="value">{{ campground.name }}</span>
                      </div>
                      <div class="wrapper" v-if="campground.street1">
                        <span>{{ campground.street1 }}</span>
                      </div>
                      <div class="wrapper" v-if="campground.street2">
                        <span>{{ campground.street2 }}</span>
                      </div>
                      <div class="wrapper" v-if="campground.city">
                        <span>{{ campground.zip_code }} {{ campground.city }}</span>
                      </div>
                      <div class="wrapper" v-if="campground.country">
                        <span>{{ getCountryByCode(campground.country) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section class="summary__step summary__step--no-border">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t('progress.step1') }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <div class="step__value">
                      <div class="wrapper" v-if="dates[0]">
                        <span class="label">{{ $t('generic.labels.arrival') }}: </span>
                        <span class="value">{{ getFormattedDate(dates[0]) }}</span>
                      </div>
                      <div class="wrapper" v-if="dates[1]">
                        <span class="label">{{ $t('generic.labels.departure') }}: </span>
                        <span class="value">{{ getFormattedDate(dates[1]) }}</span>
                      </div>
                      <div class="wrapper" v-if="dates[0] && dates[1]">
                        <span class="label">{{ $t('generic.labels.nights') }}: </span>
                        <span class="value">{{ numNights }}</span>
                      </div>

                      <div class="wrapper margin-top">
                        <div class="wrapper" v-for="(person, index) of getGuestData()" :key="index">
                          <span class="value">{{ person.amount }}x {{ person.name }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section class="summary__step" v-if="getUserCampsites">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t('progress.step2') }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <div class="step__value step__value--margin-bottom"
                         v-for="(value, key) in getUserCampsites"
                         :key="key">
                      <div class="value" v-if="Object.keys(getUserCampsites).length > 1">
                        {{ key }}
                        <small>({{ $t('generic.labels.nights') }}: {{ value[0].days }})</small>
                      </div>
                      <div class="wrapper d-flex justify-content-between no-padding"
                           v-for="(campsite, index) in value"
                           :key="index">
                        <span>{{ campsite.name }}</span>
                        <span class="prices">{{
                            campsite.price | currency(currency.code, language.code)
                          }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section class="summary__step summary__step--border-bottom" v-if="getGuestBySeason">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t('pages.bookingSummary.priceDetailsHeadline') }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <div class="step__value step__value--margin-bottom"
                         v-for="(value, key) in getGuestBySeason"
                         :key="key">
                      <div class="value" v-if="Object.keys(getGuestBySeason).length > 1">
                        {{ key }}
                        <small>({{ $t('generic.labels.nights') }}: {{ value[0].days }})</small>
                      </div>
                      <div class="wrapper d-flex justify-content-between no-padding"
                           v-for="(person, index) in value"
                           :key="index">
                        <span>{{ person.amount }}x {{ person.name }}</span>
                        <div class="prices">
                          <span class="price__value" v-if="!person.included">{{
                              person.price | currency(currency.code, language.code)
                            }}</span>
                          <span class="price__value"
                                v-if="person.included">{{ $t('generic.labels.included') }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- subtotal start -->
              <section class="summary__sum d-flex justify-content-between align-items-center">
                <div class="sum-label">
                  {{ $t('generic.labels.subtotal') }}
                  <CiTooltip class="tooltip">
                    <template slot="title">{{ $t('tooltips.subtotal.title') }}</template>
                    <template slot="content">
                      <p v-html="$t('tooltips.subtotal.content')"></p>
                    </template>
                  </CiTooltip>
                </div>
                <div class="sum-value">
                  {{ price.subtotal | currency(currency.code, language.code) }}
                </div>
              </section>
              <!-- subtotal end -->

              <section class="summary__step" v-if="getUserExtras && getUserExtras.length > 0">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t('progress.step3') }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <div class="step__value">
                      <div class="wrapper d-flex justify-content-between no-padding"
                           v-for="extra in getUserExtras"
                           :key="extra.object_id">
                        <div class="label" v-html="extra.description"></div>
                        <div class="label" v-if="extra.price !== 0">
                          {{ extra.price | currency(currency.code, language.code) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section class="summary__step"
                       v-if="getMandatoryExtrasPriceNotZero && getMandatoryExtrasPriceNotZero.length > 0">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $tc('generic.labels.mandatory_extra', 0) }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <div class="step__value">
                      <div class="wrapper d-flex justify-content-between no-padding"
                           v-for="mandatory in getMandatoryExtrasPriceNotZero"
                           :key="mandatory.object_id">
                        <div class="label" v-html="mandatory.description"></div>
                        <div class="label" v-if="mandatory.price !== 0">{{
                            mandatory.price | currency(currency.code, language.code)
                          }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section class="summary__step">
                <div class="row">
                  <div class="col-sm-4">
                    <div class="step__label">
                      {{ $t('progress.step4') }}
                    </div>
                  </div>
                  <div class="col-sm-8">
                    <div class="step__value">
                      <div class="wrapper" v-if="billing.name && billing.lastname">
                        {{ billing.name }} {{ billing.lastname }}
                      </div>
                      <div class="wrapper" v-if="billing.street">
                        {{ billing.street }}
                      </div>
                      <div class="wrapper" v-if="billing.street_additional">
                        {{ billing.street_additional }}
                      </div>
                      <div class="wrapper" v-if="billing.zip && billing.city">
                        {{ billing.zip }} {{ billing.city }}
                      </div>
                      <div class="wrapper" v-if="billing.country">
                        {{ billing.country.label }}
                      </div>
                      <div class="wrapper margin-top" v-if="billing.phone">
                        {{ billing.phone }}
                      </div>
                      <div class="wrapper" v-if="billing.mail">
                        {{ billing.mail }}
                      </div>
                    </div>
                  </div>

                  <div class="col-sm-4" v-if="Object.keys(travellers).length > 0">
                    <div class="step__label step__label--top">
                      {{ $t('generic.labels.travellers') }}
                    </div>
                  </div>
                  <div class="col-sm-8" v-if="Object.keys(travellers).length > 0">
                    <div class="step__value step__value--inter">
                      <div class="wrapper margin-top" v-for="(traveller, index) in travellers"
                           :key="index">
                        <div>{{ traveller.first_name }} {{ traveller.last_name }}</div>
                        <span>{{ getFormattedDate(traveller.birthdate) }}</span>
                      </div>
                    </div>
                  </div>

                  <div class="col-sm-4" v-if="car.license_plate || car.length || car.width">
                    <div class="step__label step__label--top">
                      {{ $t('generic.labels.car') }}
                    </div>
                  </div>
                  <div class="col-sm-8" v-if="car.license_plate || car.length || car.width">
                    <div class="step__value step__value--inter">
                      <div class="wrapper">
                        <div v-if="car.license_plate">{{ car.license_plate }}</div>
                        <div v-if="car.length">{{ car.length }} {{
                            $t('generic.labels.length')
                          }}
                        </div>
                        <div v-if="car.width">{{ car.width }} {{ $t('generic.labels.width') }}</div>
                      </div>
                    </div>
                  </div>

                  <div class="col-sm-4" v-if="billing.message">
                    <div class="step__label step__label--top">
                      {{ $t('generic.labels.message_to_campground') }}
                    </div>
                  </div>
                  <div class="col-sm-8" v-if="billing.message">
                    <div class="step__value step__value--inter">
                      {{ billing.message }}
                    </div>
                  </div>
                </div>
              </section>

              <CiSum class="summary__sum d-none d-lg-flex"
                     :campgroundCurrency="campground.currency"
                     :confirmation="confirmation"
                     :isCompleted="isCompleted"
                     :language="language"
                     :price="confirmation.pricelist"
                     :userCurrency="currency"
                     slot="sum">
              </CiSum>
              <div class="summary__warning"
                   v-if="getMandatoryExtrasPriceZero && getMandatoryExtrasPriceZero.length > 0">
                <div class="warning-title">
                  <iconWarning class="icon"></iconWarning>
                  {{ $t('pages.checkoutStepThree.infoBox.copy') }}
                </div>
                <div class="warning-content">
                  <div v-for="mandatory in getMandatoryExtrasPriceZero" :key="mandatory.object_id">
                    <div class="label" v-html="mandatory.description"></div>
                  </div>
                </div>
              </div>
            </div>

            <CiSum class="d-print-none"
                   :campgroundCurrency="campground.currency"
                   :confirmation="confirmation"
                   :isCompleted="isCompleted"
                   :isMobile="true"
                   :language="language"
                   :price="confirmation.pricelist"
                   :userCurrency="currency"
                   slot="sum">
            </CiSum>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapGetters, mapActions} from 'vuex'
import bookingDataMixin from '@/mixins/bookingDataMixin'
import bookingPriceMixin from '@/mixins/bookingPriceMixin'
import {
  CiButton,
  CiCampgroundInfo,
  CiInfoBox,
  CiProgressBar,
  CiProgressNavigation,
  CiSum,
  CiTooltip,
} from '@/components'
import * as ics from 'ics'
import iconArrowWhite from '@/assets/icons/arrow-white.svg'
import iconCheckmarkWhite from '@/assets/icons/checkmark-white.svg'
import iconWarning from '@/assets/icons/warning-orange.svg'

export default {
  name: 'BookingConfirmation',
  mixins: [bookingDataMixin, bookingPriceMixin],
  components: {
    CiButton,
    CiCampgroundInfo,
    CiInfoBox,
    CiProgressBar,
    CiProgressNavigation,
    CiSum,
    CiTooltip,
    iconArrowWhite,
    iconCheckmarkWhite,
    iconWarning,
  },
  data: () => {
    return {
      icsUrl: null,
      _pollTimer: null,
      _pollCount: 0,
      _pollMax: 15,
    }
  },
  created() {
    this.setLoader(false)

    const start = this.dates[0].split('-').map(val => parseInt(val))
    const end = this.dates[1].split('-').map(val => parseInt(val))

    const event = {
      start,
      end,
      title: this.campground.name,
    }

    ics.createEvent(event, (error, value) => {
      if (error) {
        console.log(error)
        return
      }

      const blob = new Blob([value], {
        type: 'text/calendar',
      })
      this.icsUrl = URL.createObjectURL(blob)
    })
  },
  mounted() {

    // dagenrous, might not work on all browsers.
    window.history.pushState(null, "", window.location.href);
    window.onpopstate = function () {
        window.history.pushState(null, "", window.location.href);
    };

    setTimeout(() => {
      window.sessionStorage.removeItem('vuex')
    }, 500)

    // Start polling if we are in a pending state (webhook not processed yet)
    if (this.isPending) {
      this.startPolling()
    }
  },
  beforeDestroy() {
    this.stopPolling()
  },
  computed: {
    ...mapGetters('application', {
      activeStep: 'activeStep',
      currency: 'currency',
      language: 'language',
    }),

    ...mapGetters('campground', {
      campground: 'campground',
      countries: 'countries',
      currencies: 'currencies',
      urls: 'urls',
    }),

    ...mapGetters('booking', {
      billing: 'billing',
      car: 'car',
      confirmation: 'confirmation',
      dates: 'dates',
      isCompleted: 'isCompleted',
      price: 'price',
      selectedCampsite: 'selectedCampsite',
      selectedPersons: 'selectedPersons',
      travellers: 'travellers',
    }),

    isPending() {
      try {
        return (
          this.confirmation &&
          this.confirmation.booking_status &&
          this.confirmation.booking_status !== 'confirmed'
        )
      } catch (e) {
        return false
      }
    },
  },
  methods: {
    ...mapActions('application', {
      setLoader: 'setLoader',
    }),
    ...mapActions('booking', {
      getBookingDetails: 'getBookingDetails',
    }),

    getGuestData() {
      return this.confirmation.booking_data.campsites[0].persons
    },

    printPage() {
      window.print()
    },

    startPolling() {
      this.stopPolling()
      this._pollCount = 0
      this._pollTimer = setInterval(this.pollOnce, 1000)
    },
    stopPolling() {
      if (this._pollTimer) {
        clearInterval(this._pollTimer)
        this._pollTimer = null
      }
    },
    async pollOnce() {
      try {
        this._pollCount += 1
        const res = await this.getBookingDetails(this.$route.params.bookingId)
        if (res && res.booking_status === 'confirmed') {
          this.stopPolling()
          // Data in store is updated by action; UI will re-render automatically
        } else if (this._pollCount >= this._pollMax) {
          // Give up polling; optionally route to timeout page
          this.stopPolling()
          this.$router.push({
            name: 'ConfirmationTimeout',
            params: {
              language: this.$route.params.language,
              campground: this.$route.params.campground,
              bookingId: this.$route.params.bookingId,
            },
          })
        }
      } catch (e) {
        // Ignore single poll errors, continue until max
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.booking-confirmation {
  min-height: 100vh;

  @include media-breakpoint-up(lg) {
    > .container-fluid {
      padding: 0;
    }

    > .container-fluid,
    > .container-fluid > .row {
      min-height: 100vh;
    }
  }

  @media print {
    min-height: 0;
  }
}

.booking-confirmation__interaction {
  background: #f5f5f5;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  position: relative;

  @include media-breakpoint-up(md) {
    padding: 0 20px 0;
  }

  @include media-breakpoint-up(lg) {
    padding: 0 30px 0;
  }

  @include media-breakpoint-up(xl) {
    padding: 0 35px 0;
  }

  @media print {
    display: block;
  }

  .booking__headline {
    @include font-format(1rem, 1.25rem);

    margin: 0 0 15px 0;
  }

  .booking__subline {
    margin: 0;
  }
}

.booking-confirmation__subheading {
  @include font-format(1.625rem, 1.875rem);

  margin: 40px 0 20px;
  text-align: center;

  @media print {
    page-break-before: always;
    padding-top: 30px;
    margin: 0 0 20px;
  }
}

.booking {
  border-color: var(--primary-color);
  margin: 0;
  padding: 20px;
  position: relative;
  text-align: center;

  @include media-breakpoint-up(md) {
    padding: 35px;
    text-align: left;
  }

  &.booking--summary {
    padding-top: 10px;
    margin-bottom: 120px;
    text-align: left;

    @include media-breakpoint-up(lg) {
      margin-bottom: 30px;
    }

    @media print {
      margin: 0;

      .col-sm-4 {
        flex: 0 0 33.33333%;
        max-width: 33.33333%;
      }

      .col-sm-8 {
        flex: 0 0 66.66667%;
        max-width: 66.66667%;
      }
    }
  }

  @media print {
    padding: 20px;
    text-align: center;

    .print-full {
      flex: 0 0 100%;
      max-width: 100%;
    }
  }
}

.booking__status-icon {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  margin-bottom: 20px;

  @include media-breakpoint-up(md) {
    margin-bottom: 0;
  }

  .status__circle {
    background: var(--primary-color);
    border-radius: 50%;
    display: flex;
    height: 60px;
    justify-content: center;
    opacity: 1;
    width: 60px;

    .icon {
      width: 35px;
      height: auto;
    }
  }
}

.booking__status-number {
  .booking-number {
    @include font('gotham_mediumregular');
    @include font-format(2rem, 2rem);

    color: var(--primary-color);
  }
}

.booking__download {
  margin-top: 20px;
}

.booking__status-download-button,
.booking__status-download-text {
  align-items: center;
  display: flex;
  height: 100%;

  a:hover {
    text-decoration: none;
  }

  .booking__headline {
    margin: 0;
  }
}

.download-button {
  margin: 20px auto 0;

  @include media-breakpoint-up(md) {
    margin: 0;
  }
}

.summary__step {
  @include font('gotham_bookregular');
  @include font-format(0.875rem, 1.125rem);

  border-top: 1px solid #979797;
  padding: 25px 0;
  position: relative;

  .step__label {
    @include font('gotham_mediumregular');
    @include font-format(0.875rem, 1.125rem);

    margin: 0 0 20px 0;

    &.step__label--inter {
      @include font('gotham_bookregular');

      margin: 0;
    }

    &.step__label--top {
      margin-top: 20px;
    }
  }

  .step__value {
    &.step__value--inter {
      margin-top: 20px;
    }

    &.step__value--margin-bottom {
      margin-bottom: 15px;

      &:last-child {
        margin-bottom: 0;
      }
    }

    .wrapper {
      padding-right: 40px;

      @include media-breakpoint-up(md) {
        padding-right: 70px;
      }

      &.margin-top {
        margin-top: 20px;
      }

      &.no-padding {
        padding: 0;
      }
    }

    .value {
      @include font('gotham_mediumregular');
      @include font-format(0.875rem, 1.125rem);

      small {
        @include font('gotham_bookregular');
      }
    }

    .prices {
      display: block;
    }

    .season__label {
      margin-bottom: 10px;
    }
  }

  .step__edit {
    position: absolute;
    top: 20px;
    right: 0;
    cursor: pointer;
  }

  &.summary__step--border-bottom {
    border-bottom: 1px solid #979797;
  }

  &.summary__step--no-border {
    border: 0;
  }
}

.summary__sum {
  background-color: rgba(155, 155, 155, 0.1);
  min-height: 50px;
  margin: 10px 0;

  .sum-label {
    display: flex;
    align-items: center;
    margin-left: 15px;
    height: 100%;
  }

  .sum-value {
    margin-right: 15px;
  }

  @media print {
    color: black !important;
    display: flex !important;
  }
}

.summary__warning {
  @include font('gotham_bookregular');
  @include font-format(0.75rem, 1.25rem);

  background-color: #fefcdd;
  border: 1px solid #f8e71c;
  margin: 15px 0 0;

  .warning-title {
    position: relative;
    background-color: #f8e71c66;
    padding: 10px 25px 10px 60px;

    .icon {
      position: absolute;
      left: 20px;
      top: 10px;
      width: 20px;
      height: auto;
    }
  }

  .warning-content {
    padding: 10px 25px 10px 60px;
  }
}
</style>
